import { useContext } from "react";
import { ValidationError, validationFailed, ValidationResult } from "sprint-common-web";

import { CreateEntityResponse } from "../../../common/api/common/CreateEntityResponse";
import { AppConfigContext } from "../../../core/configuration/Configuration";
import { useAuthorizedFetch } from "../../../core/data-access/useAuthorizedFetch";
import { CreateMovieRequest } from "./CreateMovieRequest";
import { CreateMovieVersionRequest } from "./CreateMovieVersionRequest";
import { GetMovieByIdResponse } from "./GetMovieByIdResponse";
import { GetMovieListRequest } from "./GetMovieListRequest";
import { GetMovieListResponse } from "./GetMovieListResponse";
import { GetMoviesBySimilarTitleResponse } from "./GetMoviesBySimilarTitleResponse";
import { MergeMoviesRequest } from "./MergeMoviesRequest";
import { MergeMoviesResponse } from "./MergeMoviesResponse";
import { MergeMovieVersionsRequest } from "./MergeMovieVersionsRequest";
import { MergeMovieVersionsResponse } from "./MergeMovieVersionsResponse";
import { UpdateMovieDetailsRequest } from "./UpdateMovieDetailsRequest";
import { UpdateMovieVersionDetailsRequest } from "./UpdateMovieVersionDetailsRequest";
import { ValidateCreateMovieVersionRequest } from "./ValidateCreateMovieVersionRequest";
import { ValidateMergeMoviesRequest } from "./ValidateMergeMoviesRequest";
import { ValidateMergeMovieVersionsRequest } from "./ValidateMergeMovieVersionsRequest";
import { ValidateMergeMovieVersionsResponse } from "./ValidateMergeMovieVersionsResponse";
import { ValidateMovieTitleRequest } from "./ValidateMovieTitleRequest";
import { ValidateUpdateMovieVersionDetailsRequest } from "./ValidateUpdateMovieVersionDetailsRequest";

export const useAssetsManageMovieApi = () => {
    const appConfigContext = useContext(AppConfigContext);
    const { httpGet, httpPost, httpPut } = useAuthorizedFetch();
    const baseUrl = `${appConfigContext.catalogueConfig.apiUrl}/movies/manage`;

    const getMovieList = async (query: GetMovieListRequest): Promise<GetMovieListResponse> => {
        return httpGet<GetMovieListResponse>(`${baseUrl}/search`, query);
    };

    const getMovieById = async (movieId: string): Promise<GetMovieByIdResponse> => {
        return httpGet<GetMovieByIdResponse>(`${baseUrl}/${movieId}`);
    };

    const createMovie = async (createMovieRequest: CreateMovieRequest): Promise<CreateEntityResponse> => {
        return httpPost<CreateEntityResponse>(`${baseUrl}`, createMovieRequest);
    };

    const validateCreateMovieVersion = async (
        validateCreateMovieVersionRequest: ValidateCreateMovieVersionRequest
    ): Promise<ValidationResult> => {
        return httpGet(
            `${baseUrl}/${validateCreateMovieVersionRequest.movieId}/versions/validate-create`,
            validateCreateMovieVersionRequest
        );
    };

    const createMovieVersion = async (
        createMovieVersionRequest: CreateMovieVersionRequest,
        movieId: string
    ): Promise<CreateEntityResponse> => {
        return httpPut<CreateEntityResponse>(`${baseUrl}/${movieId}/versions`, createMovieVersionRequest);
    };

    const updateMovieDetails = async (movieId: string, details: UpdateMovieDetailsRequest): Promise<void> => {
        return httpPut(`${baseUrl}/${movieId}`, details);
    };

    const validateUpdateMovieVersionDetails = async (
        movieId: string,
        versionId: string,
        details: ValidateUpdateMovieVersionDetailsRequest
    ): Promise<ValidationResult> => {
        try {
            return await httpGet(`${baseUrl}/${movieId}/versions/${versionId}/validate-update`, details);
        } catch (error) {
            if (error instanceof ValidationError) return validationFailed(error.failures);

            throw error;
        }
    };

    const updateMovieVersionDetails = async (
        movieId: string,
        versionId: string,
        details: UpdateMovieVersionDetailsRequest
    ): Promise<void> => {
        return httpPut(`${baseUrl}/${movieId}/versions/${versionId}`, details);
    };

    const getMoviesBySimilarTitle = async (
        request: ValidateMovieTitleRequest,
        signal: AbortSignal
    ): Promise<GetMoviesBySimilarTitleResponse> => {
        return httpGet<GetMoviesBySimilarTitleResponse>(`${baseUrl}/similar`, request, signal);
    };

    const validateMergeVersions = async (
        movieId: string,
        request: ValidateMergeMovieVersionsRequest
    ): Promise<ValidateMergeMovieVersionsResponse> => {
        return httpGet<ValidateMergeMovieVersionsResponse>(`${baseUrl}/${movieId}/versions/validate-merge`, request);
    };

    const mergeVersions = async (
        movieId: string,
        request: MergeMovieVersionsRequest
    ): Promise<MergeMovieVersionsResponse> => {
        return httpPut<MergeMovieVersionsResponse>(`${baseUrl}/${movieId}/versions/merge`, request);
    };

    const validateMergeMovies = async (request: ValidateMergeMoviesRequest): Promise<ValidationResult> => {
        return httpGet<ValidationResult>(`${baseUrl}/validate-merge`, request);
    };

    const mergeMovies = async (request: MergeMoviesRequest): Promise<MergeMoviesResponse> => {
        return httpPut<MergeMoviesResponse>(`${baseUrl}/merge`, request);
    };

    return {
        getMovieList,
        getMovieById,
        createMovie,
        createMovieVersion,
        validateCreateMovieVersion,
        updateMovieDetails,
        validateUpdateMovieVersionDetails,
        updateMovieVersionDetails,
        getMoviesBySimilarTitle,
        validateMergeVersions,
        mergeVersions,
        validateMergeMovies,
        mergeMovies
    };
};
